import type { EmployeeWeeklySchedule } from './types';

export const SHIFT_PUBLICATION_STORAGE_KEY = 'garderie-published-shifts';

type PublishedShiftsStore = Record<
  string,
  { schedule: EmployeeWeeklySchedule; publishedAt: string }
>;

function publicationKey(weekKey: string, employeeId: string): string {
  return `${weekKey}:${employeeId}`;
}

function readStore(): PublishedShiftsStore {
  try {
    const raw = localStorage.getItem(SHIFT_PUBLICATION_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PublishedShiftsStore;
  } catch {
    return {};
  }
}

function writeStore(store: PublishedShiftsStore): void {
  try {
    localStorage.setItem(SHIFT_PUBLICATION_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

function cloneSchedule(schedule: EmployeeWeeklySchedule): EmployeeWeeklySchedule {
  return JSON.parse(JSON.stringify(schedule)) as EmployeeWeeklySchedule;
}

export function publishEmployeeWeekSchedule(
  weekKey: string,
  employeeId: string,
  schedule: EmployeeWeeklySchedule
): void {
  const store = readStore();
  store[publicationKey(weekKey, employeeId)] = {
    schedule: cloneSchedule(schedule),
    publishedAt: new Date().toISOString(),
  };
  writeStore(store);
}

type EmployeeIdKey = string | readonly string[];

function asIdList(employeeId: EmployeeIdKey): string[] {
  return typeof employeeId === 'string' ? [employeeId] : [...employeeId];
}

/** Tries each alias (Convex id, then optional legacyId) until a publish is found. */
export function getPublishedEmployeeWeekSchedule(
  weekKey: string,
  employeeId: EmployeeIdKey
): EmployeeWeeklySchedule | null {
  const store = readStore();
  for (const id of asIdList(employeeId)) {
    const entry = store[publicationKey(weekKey, id)];
    if (entry) return entry.schedule;
  }
  return null;
}

export function getPublishedAt(weekKey: string, employeeId: EmployeeIdKey): string | null {
  const store = readStore();
  for (const id of asIdList(employeeId)) {
    const publishedAt = store[publicationKey(weekKey, id)]?.publishedAt;
    if (publishedAt) return publishedAt;
  }
  return null;
}

export function isWeekPublishedForEmployee(
  weekKey: string,
  employeeId: EmployeeIdKey
): boolean {
  return getPublishedEmployeeWeekSchedule(weekKey, employeeId) != null;
}

export function notifyShiftsPublished(): void {
  window.dispatchEvent(new CustomEvent('garderie:shifts-published'));
}
